import PropTypes from "prop-types"
import { useEffect, useMemo } from "react"
// @mui
import { Box, Drawer, GlobalStyles, Stack } from "@mui/material"
import { styled } from "@mui/material/styles"
// hooks
import useCollapseDrawer from "minimals-template/components/@hooks/useCollapseDrawer"
import useResponsive from "minimals-template/components/@hooks/useResponsive"
import NavSectionVertical from "./nav-section-vertical"
import Scrollbar from "minimals-template/components/Scrollbar"
// config
import { NAVBAR } from "config"
// components
//
import { CollapseNavBar, CollapseSideBar, RefreshNavBar } from "event-definitions"
import { getLeftBarNavigationItems } from "ioc"
import CollapseButton from "./collapse-button"
import { NavbarFooter, NavbarHeader } from "slot-definitions"
import { ApplicationSwitcher } from "./application-switcher"
import { useApplicationSwitcherEligible } from "lib/@components/application-switcher/use-application-switcher-eligible"
import { If } from "lib/@components/switch"
import { CompanySwitcher } from "lib/@components/company-switcher/company-switcher"
import { useLocation } from "react-router"
import { getTopLevelPath } from "lib/getTopLevelPath"
import { applyNavOverrides } from "./nav-overrides"
import { COLLAPSE_WIDTH_VARIABLE, REDESIGN_COLLAPSE_WIDTH } from "./collapse-width"
import { NavFontFace, navFontSx } from "./nav-font"
// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
    position: "relative",
    [theme.breakpoints.up("lg")]: {
        flexShrink: 0,
        transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.shorter,
        }),
    },
}))

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
    isOpenSidebar: PropTypes.bool,
    onCloseSidebar: PropTypes.func,
}

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
    CollapseSideBar.useEvent(onCloseSidebar)

    const appRoute = getTopLevelPath() || "app"

    RefreshNavBar.useRefresh()
    const navigation = applyNavOverrides(getLeftBarNavigationItems())
    navigation.sortBy((n) => n.subPaths?.priority ?? 100)

    let groups = navigation
        // An item with an explicit subPaths.apps list shows only in those applications,
        // regardless of its path — this is how cross-app links (e.g. System Admin's
        // "Home" back to /app) avoid also appearing in the app their path belongs to.
        .filter((f) => (f.subPaths?.apps ? f.subPaths.apps.includes(appRoute) : f.path?.startsWith(`/${appRoute}`)))
        .filter((f) => !f.subPaths?.isVisible || f.subPaths.isVisible())
        .groupBy("subPaths.group")

    let navConfig = [{ group: null, subheader: null, items: groups.undefined }]
    delete groups.undefined
    navConfig = [
        ...navConfig,
        ...Object.entries(groups).map(([subheader, items]) => ({
            group: subheader,
            subheader: items[0]?.subPaths?.groupContent ?? subheader,
            items: items || [],
        })),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const finalConfig = useMemo(() => navConfig, [JSON.stringify(navConfig)])

    const location = useLocation()

    const isDesktop = useResponsive("up", "lg")
    const { isCollapse, collapseClick, onToggleCollapse, onCloseCollapse } = useCollapseDrawer()
    const showApplicationSwitcher = useApplicationSwitcherEligible()

    CollapseNavBar.useEvent(onCloseCollapse)

    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    const renderContent = (
        <>
            <Stack
                spacing={0}
                sx={{
                    pt: "calc(env(safe-area-inset-top) + 12px)",
                    pb: 1,
                    px: 0,
                    flexShrink: 0,
                    ...(isCollapse && { alignItems: "center" }),
                }}
            >
                <NavbarHeader.Slot />
            </Stack>
            <If value={showApplicationSwitcher}>
                <ApplicationSwitcher isCollapse={isCollapse} />
            </If>
            <CompanySwitcher isCollapse={isCollapse} />
            <Scrollbar
                sx={{
                    height: 1,
                    "& .simplebar-content": { height: 1, display: "flex", flexDirection: "column" },
                }}
            >
                <NavSectionVertical
                    data-cy="left-navbar"
                    navConfig={finalConfig}
                    isCollapse={isCollapse}
                    id="navbarVertical"
                />
                <NavbarFooter.Slot />
                <Box sx={{ flexGrow: 1 }} />
            </Scrollbar>
        </>
    )

    return (
        <>
            <GlobalStyles styles={{ ":root": { [COLLAPSE_WIDTH_VARIABLE]: `${REDESIGN_COLLAPSE_WIDTH}px` } }} />
            <NavFontFace />
            <RootStyle
                sx={{
                    ...navFontSx,
                    width: {
                        lg: isCollapse ? REDESIGN_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
                    },
                    ...(collapseClick && {
                        position: "absolute",
                    }),
                }}
            >
                {!isDesktop && (
                    <Drawer
                        open={isOpenSidebar}
                        onClose={onCloseSidebar}
                        PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH, ...navFontSx } }}
                    >
                        {renderContent}
                    </Drawer>
                )}

                {isDesktop && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 30,
                            right: -18,
                            lineHeight: 0,
                            zIndex: (t) => t.zIndex.drawer + 1,
                        }}
                    >
                        <CollapseButton
                            data-cy="navItem-collapseButton"
                            onToggleCollapse={onToggleCollapse}
                            collapseClick={collapseClick}
                        />
                    </Box>
                )}

                {isDesktop && (
                    <Drawer
                        open
                        variant="persistent"
                        PaperProps={{
                            sx: {
                                width: NAVBAR.DASHBOARD_WIDTH,
                                borderRightStyle: "dashed",
                                bgcolor: "background.default",
                                transition: (theme) =>
                                    theme.transitions.create("width", {
                                        duration: (!collapseClick && theme.transitions.duration.standard) || 0,
                                    }),
                                ...(isCollapse && {
                                    width: REDESIGN_COLLAPSE_WIDTH,
                                }),
                            },
                        }}
                    >
                        {renderContent}
                    </Drawer>
                )}
            </RootStyle>
        </>
    )
}
